import { create } from 'zustand';
import { STORAGE_KEYS } from '@/constants/storage';

/**
 * Small, user-level UI preferences that persist across sessions but are not part of
 * any workspace document. Reads/writes are best-effort: a blocked localStorage just
 * means defaults.
 */
export interface SettingsStore {
  readonly gridVisible: boolean;
  readonly minimapVisible: boolean;
  readonly reduceMotion: boolean;
  readonly developerMode: boolean;
  readonly setGridVisible: (v: boolean) => void;
  readonly setMinimapVisible: (v: boolean) => void;
  readonly setReduceMotion: (v: boolean) => void;
  readonly setDeveloperMode: (v: boolean) => void;
}

interface Persisted {
  gridVisible?: boolean;
  minimapVisible?: boolean;
  reduceMotion?: boolean;
  developerMode?: boolean;
}

const load = (): Persisted => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEYS.settings);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const p = parsed as Record<string, unknown>;
    return {
      ...(typeof p.gridVisible === 'boolean' ? { gridVisible: p.gridVisible } : {}),
      ...(typeof p.minimapVisible === 'boolean' ? { minimapVisible: p.minimapVisible } : {}),
      ...(typeof p.reduceMotion === 'boolean' ? { reduceMotion: p.reduceMotion } : {}),
      ...(typeof p.developerMode === 'boolean' ? { developerMode: p.developerMode } : {}),
    };
  } catch {
    return {};
  }
};

const save = (s: Persisted): void => {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEYS.settings, JSON.stringify(s));
  } catch {
    /* storage unavailable: settings live for the session only */
  }
};

const initial = load();

export const useSettingsStore = create<SettingsStore>()((set, get) => {
  const persist = () => {
    const { gridVisible, minimapVisible, reduceMotion, developerMode } = get();
    save({ gridVisible, minimapVisible, reduceMotion, developerMode });
  };
  return {
    gridVisible: initial.gridVisible ?? true,
    minimapVisible: initial.minimapVisible ?? true,
    reduceMotion: initial.reduceMotion ?? false,
    developerMode: initial.developerMode ?? false,
    setGridVisible: (gridVisible) => {
      if (get().gridVisible === gridVisible) return;
      set({ gridVisible });
      persist();
    },
    setMinimapVisible: (minimapVisible) => {
      if (get().minimapVisible === minimapVisible) return;
      set({ minimapVisible });
      persist();
    },
    setReduceMotion: (reduceMotion) => {
      if (get().reduceMotion === reduceMotion) return;
      set({ reduceMotion });
      persist();
    },
    setDeveloperMode: (developerMode) => {
      if (get().developerMode === developerMode) return;
      set({ developerMode });
      persist();
    },
  };
});
